// Settings registry seed (settings-registry-seed.md). Phase 0 declared five keys; Phase 2 (track 2A) adds every key The
// Owl Club has stored (ops plans/2026-10-10-owl-club-usage/inventory.md §7), the pricing program and its presets, and
// the store-and-forward limits. C3 seeds every remaining legacy key. `settings/legacy.ts` maps legacy stores onto it.
import { z } from 'zod';
import { money, rate } from '@radish/primitives';
import type { Origin } from '../define/meta.ts';
import { defineSettings, type SettingDef, type SettingScope } from '../define/settings.ts';
import type { PermissionKey } from '../permissions/catalog.ts';
import { Money, QuarterHourTime, Rate } from '../primitives/codecs.ts';

interface Meta {
  readonly label: string;
  readonly category: string;
  readonly permission: PermissionKey;
  readonly consumers: readonly string[];
  /** The legacy key in `store` this replaces, or null for a new key. */
  readonly legacy: string | null;
  readonly store?: 'locations.config' | 'locations' | 'devices.config';
  readonly origin?: Origin;
  readonly adminOnly?: boolean;
  readonly replication?: SettingDef['replication'];
  readonly scope?: readonly SettingScope[];
  /** `SettingDef.lockedBy`: who controls the value instead of the register (DS-077). */
  readonly lockedBy?: string;
}

const declare = <S extends z.ZodType>(schema: S, value: z.output<S>, m: Meta, scope: readonly SettingScope[], origin: Origin): SettingDef<S> => ({
  schema, default: value, scope: m.scope ?? scope, origin: m.origin ?? origin, overridable: false, permission: m.permission,
  adminOnly: m.adminOnly ?? false, category: m.category, label: m.label, consumers: m.consumers, replication: m.replication ?? 'synced',
  legacy: m.legacy === null ? null : { store: m.store ?? 'locations.config', key: m.legacy }, since: 2,
  ...(m.lockedBy === undefined ? {} : { lockedBy: m.lockedBy }),
});

/** A location key (legacy `locations.config` unless `store` says otherwise), written from the cloud or the node. */
const atLocation = <S extends z.ZodType>(schema: S, value: z.output<S>, m: Meta): SettingDef<S> => declare(schema, value, m, ['location'], 'any');

/**
 * A device key (legacy `devices.config`): the register may write its own value offline (`origin: 'device'`, T2), and
 * the backoffice writes it with Hardware Settings. Defaults are what a legacy register got for a key it never stored:
 * the API's DeviceSettings default, which GET /v1/config/{id} merges over the stored config, else the register's own
 * settingsDefaults (lane S, 2026-10-13: DECIDE-CON-4's "lean register" assumed the register's default was the one seen).
 * test/legacy-setting-defaults.test.ts checks every default against legacy's.
 */
/** Device keys a location may also set: the location's value applies to each register that has none of its own. */
const LAYOUT_SCOPE: readonly SettingScope[] = ['location', 'device'];

const atDevice = <S extends z.ZodType>(schema: S, value: z.output<S>, m: Omit<Meta, 'permission'>): SettingDef<S> =>
  declare(schema, value, { ...m, permission: 'hardware.manage', store: 'devices.config' }, ['device'], 'device');

/**
 * A KDS screen's own key (ops plan 2026-10-13-kds; parity kds/screens.md "Settings dialog"). Legacy keeps these in a
 * file on each screen (no API store, so `legacy: null`); here they are the screen's device settings, which the screen
 * writes offline from its Settings dialog and support can read. Defaults are legacy's `defaultConfig`.
 */
const atKds = <S extends z.ZodType>(schema: S, value: z.output<S>, label: string): SettingDef<S> =>
  declare(schema, value, { label, category: 'Kitchen Display', permission: 'hardware.manage', consumers: ['kds'], legacy: null }, ['device'], 'device');

const KdsMinutes = z.int().min(1).max(60);
const KdsSound = z.enum(['ding', 'bell', 'error']);

/** Legacy "AR" keys: only the cloud writes them, and only admins and resellers (Payment Configuration, SAF limits). */
const platform = { origin: 'cloud', adminOnly: true, permission: 'settings.platform.manage' } as const;

const checkout = { permission: 'settings.order_checkout.manage' } as const;

/** Legacy SelectColor.vue's swatches, in its order. */
export const LOCATION_COLORS = [
  'blue', 'brown', 'cyan', 'dark-blue', 'dark-green', 'deep-purple', 'gold', 'green', 'indigo', 'light-green', 'light-blue', 'maroon',
  'orange', 'pink', 'purple', 'red', 'teal', 'salmon',
] as const;
/**
 * Colours a legacy location may hold that the picker does not offer: 'lime-green' (no longer offered, "broken" there),
 * and 'gray', 'gray-alternate' and 'very-dark', which legacy's `Color` enum accepts (api `Types/Color.php`) and its back
 * office shows with their `bg-*` classes. They import as stored and show as legacy shows them (lane S, 2026-10-13).
 */
export const LEGACY_ONLY_LOCATION_COLORS = ['lime-green', 'gray', 'gray-alternate', 'very-dark'] as const;
const LocationColor = z.enum([...LOCATION_COLORS, ...LEGACY_ONLY_LOCATION_COLORS]);
const tips = { ...checkout, category: 'Tips & Gratuity' } as const;
// Legacy shows Settings › Scheduling to platform admins only (`requiresAdminRole`; G-M19, Q-SET-10), so its keys are
// written with the platform key: the page's gate is the server's too (B749, GATE-02).
const staff = { permission: 'settings.platform.manage', category: 'Scheduling' } as const;

const ReceiptSize = z.enum(['sm', 'md', 'lg']);
const CashRounding = z.enum(['none', 'dollar', 'half_dollar', 'quarter_dollar']);
/** Legacy `logout_idle_time`: None, 30 seconds, 1, 5 and 10 minutes. */
const IDLE_SECONDS: readonly number[] = [0, 30, 60, 300, 600];

const declared = defineSettings({
  'business_day.start_time': {
    schema: QuarterHourTime, default: '04:00', scope: ['location'], origin: 'any', overridable: false,
    permission: 'settings.business_day.manage', adminOnly: false, category: 'Business Day', label: 'Business day starts at',
    consumers: ['server', 'register', 'kds', 'crew'], replication: 'synced',
    effectiveDated: { history: true, effective: 'next_business_day' },
    legacy: null, since: 1,
  },
  'orders.split_check.enabled': {
    schema: z.boolean(), default: false, scope: ['location'], origin: 'any', overridable: false,
    permission: 'settings.order_checkout.manage', adminOnly: false, category: 'Orders', label: 'Allow split checks',
    consumers: ['register', 'pronto'], replication: 'synced',
    legacy: { store: 'locations.config', key: 'split_check' }, since: 1,
  },
  'orders.void.require_reason_for_sent_items': {
    schema: z.boolean(), default: false, scope: ['location'], origin: 'any', overridable: false,
    permission: 'settings.order_checkout.manage', adminOnly: false, category: 'Orders', label: 'Require a reason to void sent items',
    consumers: ['register'], replication: 'synced',
    legacy: { store: 'locations.config', key: 'void_order_item_with_reason' }, since: 1,
  },
  'orders.gratuity.auto_rate': {
    schema: Rate, default: rate(0n), scope: ['location'], origin: 'any', overridable: false,
    permission: 'settings.order_checkout.manage', adminOnly: false, category: 'Tips & Gratuity', label: 'Auto-gratuity rate',
    consumers: ['core'], replication: 'synced',
    legacy: { store: 'locations.config', key: 'auto_gratuity_percentage' }, since: 1,
  },
  'payments.tips.custom_presets': {
    // Whole-percent UI presets, kept as integers for parity with the legacy tip screen (seed conventions).
    schema: z.tuple([z.int().min(0).max(100), z.int().min(0).max(100), z.int().min(0).max(100)]),
    default: [0, 0, 0], scope: ['location'], origin: 'any', overridable: false,
    permission: 'settings.order_checkout.manage', adminOnly: false, category: 'Tips & Gratuity', label: 'Custom tip presets',
    consumers: ['register'], replication: 'synced',
    legacy: { store: 'locations.config', key: 'custom_tip_amount_1..3' }, since: 1,
  },
  // Payroll rules core's `computePayroll` takes (decide-list E, CD-L01–L05, L14–L17). Defaults are legacy's; thresholds
  // are minutes (legacy stored hours: 8, 40 and 12).
  'labor.overtime.daily.enabled': {
    schema: z.boolean(), default: true, scope: ['location'], origin: 'any', overridable: false,
    permission: 'settings.location.manage', adminOnly: false, category: 'Payroll', label: 'Daily overtime',
    consumers: ['core'], replication: 'synced',
    legacy: { store: 'locations.config', key: 'overtime_hours_day_enabled' }, since: 1,
  },
  'labor.overtime.daily.after_minutes': {
    schema: z.int().min(60).max(1440), default: 480, scope: ['location'], origin: 'any', overridable: false,
    permission: 'settings.location.manage', adminOnly: false, category: 'Payroll', label: 'Daily overtime after',
    consumers: ['core'], replication: 'synced',
    legacy: { store: 'locations.config', key: 'overtime_hours_day' }, since: 1,
  },
  'labor.overtime.weekly.enabled': {
    schema: z.boolean(), default: true, scope: ['location'], origin: 'any', overridable: false,
    permission: 'settings.location.manage', adminOnly: false, category: 'Payroll', label: 'Weekly overtime',
    consumers: ['core'], replication: 'synced',
    legacy: { store: 'locations.config', key: 'overtime_hours_week_enabled' }, since: 1,
  },
  'labor.overtime.weekly.after_minutes': {
    schema: z.int().min(60).max(10080), default: 2400, scope: ['location'], origin: 'any', overridable: false,
    permission: 'settings.location.manage', adminOnly: false, category: 'Payroll', label: 'Weekly overtime after',
    consumers: ['core'], replication: 'synced',
    legacy: { store: 'locations.config', key: 'overtime_hours_week' }, since: 1,
  },
  'labor.double_time.daily.enabled': {
    schema: z.boolean(), default: true, scope: ['location'], origin: 'any', overridable: false,
    permission: 'settings.location.manage', adminOnly: false, category: 'Payroll', label: 'Daily double time',
    consumers: ['core'], replication: 'synced',
    legacy: { store: 'locations.config', key: 'double_time_hours_day_enabled' }, since: 1,
  },
  'labor.double_time.daily.after_minutes': {
    // CD-L04: honoured in every overtime mode (legacy used 12 h in one branch and D8 + 4 h in the other).
    schema: z.int().min(60).max(1440), default: 720, scope: ['location'], origin: 'any', overridable: false,
    permission: 'settings.location.manage', adminOnly: false, category: 'Payroll', label: 'Daily double time after',
    consumers: ['core'], replication: 'synced',
    legacy: { store: 'locations.config', key: 'double_time_hours_day' }, since: 1,
  },
  'labor.seventh_day.enabled': {
    schema: z.boolean(), default: true, scope: ['location'], origin: 'any', overridable: false,
    permission: 'settings.location.manage', adminOnly: false, category: 'Payroll', label: '7th consecutive day overtime',
    consumers: ['core'], replication: 'synced',
    legacy: { store: 'locations.config', key: 'seventh_day_enabled' }, since: 1,
  },
  'labor.seventh_day.mode': {
    // CD-L02. Legacy's Seventh-day Rule radio stores it as `workday_day` ('08:00': overtime then double time, the
    // default; 'overtime': every hour at overtime), and its payroll reads it (`LocationConfig::seventhDayOption`).
    schema: z.enum(['ot_then_dt', 'all_ot']), default: 'ot_then_dt', scope: ['location'], origin: 'any', overridable: false,
    permission: 'settings.location.manage', adminOnly: false, category: 'Payroll', label: '7th day pays',
    consumers: ['core'], replication: 'synced',
    legacy: { store: 'locations.config', key: 'workday_day' }, since: 1,
  },
  'labor.meal_premium.flagging_enabled': {
    // Legacy `ca_meal_premium_flagging_enabled` (Overtime Settings › California Meal-Period Premiums): flags timecards
    // where a California meal-period premium may be owed and adds the Meal Premium column to payroll (core
    // `assessMealPremiums`). Off by default, as legacy. Compliance sign-off waits on asks A16.
    schema: z.boolean(), default: false, scope: ['location'], origin: 'any', overridable: false,
    permission: 'settings.location.manage', adminOnly: false, category: 'Payroll', label: 'Flag potential meal-period premiums',
    consumers: ['core', 'backoffice'], replication: 'synced',
    legacy: { store: 'locations.config', key: 'ca_meal_premium_flagging_enabled' }, since: 1,
  },
  'labor.workweek_start': {
    // CD-L03: ISO weekday (1 = Monday, legacy's hard-coded start); changes take effect from a future business day.
    schema: z.int().min(1).max(7), default: 1, scope: ['location'], origin: 'any', overridable: false,
    permission: 'settings.location.manage', adminOnly: false, category: 'Payroll', label: 'Workweek starts on',
    consumers: ['core', 'crew'], replication: 'synced',
    effectiveDated: { history: true, effective: 'next_business_day' },
    legacy: { store: 'locations.config', key: 'workweek_day' }, since: 1,
  },
  // B75 (ledger FIX, built switched off for payroll parity): how a back-office save (Create or Adjust Timecard) treats a
  // paid break that runs past its allowance (core `withBackOfficeOverrun`). 'legacy' is legacy's UpdateTimecardBreaks: a
  // created card never converts, and an adjust converts against the minutes alone unless the break's own row pays its
  // buffer; a definition change on an existing break steers only that save, and the break keeps its own (B1943).
  // 'fixed' converts on both saves as a punched break converts, and a definition change sticks. Platform staff only;
  // legacy has no such key.
  'labor.breaks.back_office_overrun': atLocation(z.enum(['legacy', 'fixed']), 'legacy', {
    ...platform, category: 'Payroll', label: 'Back-office breaks past their allowance', consumers: ['core'], legacy: null,
  }),

  // ---- Location: orders, tables, courses ------------------------------------------------------------------------------
  'orders.tax_profile.show_in_product_card': atLocation(z.boolean(), false, {
    ...checkout, category: 'Orders', label: 'Change tax profile from the product card', consumers: ['register'], legacy: 'change_tax_profile',
  }),
  // Legacy Cart.ts addToCart: adding a tax-exclusive item sets the whole cart's tax profile to tax-exclusive (refused
  // with a toast once a payment counts). Register Settings > Tax Settings (SETG-016).
  'orders.tax_profile.exclusive_item_override': atLocation(z.boolean(), false, {
    ...checkout, category: 'Orders', label: 'Tax-exclusive cart override', consumers: ['register'], legacy: 'override_tax_profile',
  }),
  // Legacy: Connect Display / Disconnect Display in the register's admin menu switch a paired customer display between
  // Customer Display and POS mode. Register Settings > Customer Facing Display.
  'register.customer_display.mode_switching': atLocation(z.boolean(), false, {
    ...checkout, category: 'Customer Facing Display', label: 'Allow CFD mode switching', consumers: ['register'], legacy: 'allow_cfd_mode_switching',
  }),
  'orders.gratuity.auto_enabled': atLocation(z.boolean(), false, {
    ...tips, label: 'Auto-gratuity', consumers: ['register', 'core'], legacy: 'auto_gratuity_enabled',
  }),
  // Legacy AutoGratuityStore: the minimum order subtotal, and the dining options it applies on (none named: every one).
  'orders.gratuity.auto_threshold': atLocation(Money, money(0n, 'USD'), {
    ...tips, label: 'Auto-gratuity minimum order subtotal', consumers: ['register', 'core'], legacy: 'auto_gratuity_threshold',
  }),
  'orders.gratuity.auto_dining_option_ids': atLocation(z.array(z.string().max(64)).max(100), [], {
    ...tips, label: 'Auto-gratuity dining options', consumers: ['register', 'core'], legacy: null,
  }),
  'payments.bar_tabs.attach_card': atLocation(z.boolean(), false, {
    ...checkout, category: 'Bar Tabs', label: 'Attach a card to bar tabs', consumers: ['register'], legacy: 'bar_tab_attached_cards',
  }),
  // L168: one switch for quantity split (legacy also read the Split flag posQuantityItemSplit; the import ORs them).
  'orders.split_check.split_quantity': atLocation(z.boolean(), false, {
    ...checkout, category: 'Orders', label: 'Split item quantities across checks', consumers: ['register'], legacy: 'split_item_quantity_across_checks',
  }),
  'floor.table_mapping': atLocation(z.boolean(), false, {
    permission: 'settings.location.manage', category: 'Tables', label: 'Table mapping', consumers: ['register', 'backoffice'], legacy: 'table_mapping',
  }),
  // Hostess mode (legacy behind the Split betaHostessMode as well): with table mapping on, an employee holding
  // `floor.tableAssignment` seats a party at an empty table on a server's open bank.
  'floor.hostess_mode': atLocation(z.boolean(), false, {
    permission: 'settings.location.manage', category: 'Tables', label: 'Enable hostess mode', consumers: ['register', 'backoffice'], legacy: 'hostess_mode',
  }),
  // Lane D3 (W-30): order pacing. Legacy also gated it behind the Split flag betaOrderPacing; the setting alone decides here.
  'kitchen.courses.enabled': atLocation(z.boolean(), false, {
    ...checkout, category: 'Courses', label: 'Enable Courses', consumers: ['register', 'backoffice'], legacy: 'enable_meal_courses',
  }),
  // Lane D3 (W-31): Hold on the product card, Fire All on order details, the Orders page's Held tab.
  'kitchen.hold_items.enabled': atLocation(z.boolean(), false, {
    ...checkout, category: 'Kitchen', label: 'Kitchen hold & fire', consumers: ['register', 'backoffice'], legacy: 'enable_hold_items',
  }),
  // RCP-26 (legacy register 5f03d4815, 2026-10-16): a VOID kitchen ticket when an order is voided or a sent line is removed
  // or reduced; off by default, as legacy's. Legacy's API (73b1c6086) and back office Register Settings (cfd4307b3) now
  // store both keys, so the import carries them (B1897), and Next's back office shows them in Register Settings' Kitchen
  // (B1904), where the form holds the window to a whole number of minutes from 1 to 1440 as legacy's does.
  'receipts.kitchen.void_ticket.enabled': atLocation(z.boolean(), false, {
    ...checkout, category: 'Printing', label: 'Print kitchen void tickets', consumers: ['register', 'backoffice'], legacy: 'kitchen_void_receipt_enabled',
  }),
  'receipts.kitchen.void_ticket.auto_print_minutes': atLocation(z.number().positive(), 30, {
    ...checkout, category: 'Printing', label: 'Print void tickets automatically within (minutes of the order)', consumers: ['register', 'backoffice'],
    legacy: 'kitchen_void_receipt_auto_print_window_minutes',
  }),
  // Print Options › Receipts (legacy PrintOptionsSettings.vue): blank lines between items, and item notes on customer receipts.
  'receipts.extra_line_spacing': atLocation(z.boolean(), false, {
    ...checkout, category: 'Printing', label: 'Increase vertical spacing', consumers: ['register', 'backoffice'], legacy: 'print_new_extra_line',
  }),
  'receipts.customer.show_item_notes': atLocation(z.boolean(), true, {
    ...checkout, category: 'Printing', label: 'Display item notes', consumers: ['register', 'backoffice'], legacy: 'display_item_notes_on_customer_receipts',
  }),
  'receipts.kitchen.separate_course_tickets': atLocation(z.boolean(), false, {
    ...checkout, category: 'Printing', label: 'Print separate receipts for each course', consumers: ['register', 'backoffice'],
    legacy: 'print_separate_receipts_for_each_course',
  }),
  'kitchen.courses.prompt_mode': atLocation(z.enum(['off', 'optional', 'required']), 'off', {
    ...checkout, category: 'Courses', label: 'Course prompt', consumers: ['backoffice'], legacy: 'course_prompt',
  }),
  'receipts.kitchen.course_print_mode': atLocation(z.enum(['first_fire', 'every_fire', 'never']), 'first_fire', {
    // The stored legacy key is misspelled; it is kept verbatim on the wire of the import only (settings-and-flags Q8).
    ...checkout, category: 'Printing', label: 'Print the entire kitchen receipt', consumers: ['register', 'backoffice'], legacy: 'print_enitre_kitchen_receipt',
  }),

  // ---- Location: tips and receipts ----------------------------------------------------------------------------------
  'payments.tips.use_default_presets': atLocation(z.boolean(), true, {
    ...tips, label: 'Use default tip amounts', consumers: ['register'], legacy: 'use_default_tip_amounts',
  }),
  'payments.tips.reactions_enabled': atLocation(z.boolean(), false, {
    ...tips, label: 'Tip reactions', consumers: ['register'], legacy: 'enable_tip_reactions',
  }),
  'payments.tips.no_tip_label': atLocation(z.enum(['next_time', 'no_tip']), 'next_time', {
    ...tips, label: 'No-tip button text', consumers: ['register'], legacy: 'no_tip_button_text',
  }),
  'payments.tips.signature_on_customer_display': atLocation(z.boolean(), true, {
    ...tips, label: 'Ask for a signature on the customer display', consumers: ['register'], legacy: 'display_sign_signature_on_cfd',
  }),
  'payments.receipts.carry_to_table_prompt': atLocation(z.boolean(), false, {
    ...tips, label: 'Show "carry receipt to table" on the pay-at-table device', consumers: ['register'], legacy: 'display_carry_receipt_to_table_on_ptd',
  }),
  'labor.tips.pooling_enabled': atLocation(z.boolean(), false, {
    permission: 'settings.staff.manage', category: 'Tips & Gratuity', label: 'Tip pooling', consumers: ['register', 'backoffice'], legacy: 'tip_pooling',
  }),
  'labor.reports.external_employee_id_enabled': atLocation(z.boolean(), false, {
    // Legacy `external_employee_id_enabled`: the External ID field on employees and the payroll report column.
    permission: 'settings.staff.manage', category: 'Timecards', label: 'External employee ID', consumers: ['backoffice'], legacy: 'external_employee_id_enabled',
  }),
  'labor.tips.paid_in_enabled': atLocation(z.boolean(), false, {
    // Legacy `tips_paid_in_management` (not seeded: absent reads as off): Manager Activities › Tips Paid In.
    permission: 'settings.staff.manage', category: 'Tips & Gratuity', label: 'Tips paid in', consumers: ['register', 'backoffice'], legacy: 'tips_paid_in_management',
  }),

  // ---- Location: server banks and scheduling -----------------------------------------------------------------------
  'cash.server_banks.enabled': atLocation(z.boolean(), false, {
    ...checkout, category: 'Server Cash Banks', label: 'Server cash banks', consumers: ['register', 'backoffice'], legacy: 'server_bank_drawers',
  }),
  'labor.tips.payroll_from_tenders': atLocation(z.boolean(), false, {
    ...tips, label: 'Pay tips through payroll from tenders', consumers: ['register', 'core'], legacy: 'payroll_tips_from_tenders',
  }),
  'orders.tabs.offline_open_policy': atLocation(z.enum(['manager_approval', 'allowed', 'blocked']), 'manager_approval', {
    // L35: who may open a tab by name (no card hold) while the register is offline.
    ...checkout, category: 'Bar Tabs', label: 'Opening tabs while offline', consumers: ['register'], legacy: null,
  }),
  'orders.tabs.offline_unpaid_cap': atLocation(Money, money(10_000n, 'USD'), {
    // L35: past this unpaid balance an offline tab must be paid before more is sent.
    ...checkout, category: 'Bar Tabs', label: 'Offline tab limit', consumers: ['register'], legacy: null,
  }),
  'cash.server_banks.login_on_register': atLocation(z.boolean(), false, {
    ...checkout, category: 'Server Cash Banks', label: 'Server login on the register', consumers: ['register'], legacy: 'enable_server_login_on_register',
  }),
  'cash.server_banks.closeout_without_tip_payout': atLocation(z.boolean(), false, {
    ...checkout, category: 'Server Cash Banks', label: 'Close out a bank without a tip payout', consumers: ['register'],
    legacy: 'enable_closeout_bank_without_tip_payout',
  }),
  // Legacy register useSystemUtils `hideCashForServerBank`: no cash on the Pay screen while a server bank is in use.
  'cash.server_banks.disable_cash': atLocation(z.boolean(), false, {
    ...checkout, category: 'Server Cash Banks', label: 'Disable cash payments on server banks', consumers: ['register'], legacy: 'disable_cash_on_server_banks',
  }),
  // Legacy register ServerAutoLogoutPopupStore: after a transaction on a server bank, a sign-out countdown.
  'cash.server_banks.auto_sign_out': atLocation(z.boolean(), false, {
    ...checkout, category: 'Server Cash Banks', label: 'Auto-Logout for Server Cash Banks', consumers: ['register'], legacy: 'auto_logout_for_server_banks_on_register',
  }),
  'cash.server_banks.auto_sign_out_seconds': atLocation(z.int().min(10).max(30), 10, {
    ...checkout, category: 'Server Cash Banks', label: 'Countdown timer', consumers: ['register'], legacy: 'auto_logout_for_server_banks_on_register_timer',
  }),
  'cash.management.note_required': atLocation(z.boolean(), false, {
    // Back Office › Register Settings › Cash Management: a Paid In or Paid Out needs a description on the register.
    ...checkout, category: 'Cash Management', label: 'Require Cash Management description', consumers: ['register', 'core'],
    legacy: 'cash_management_note_required',
  }),
  'labor.scheduling.enabled': atLocation(z.boolean(), false, {
    ...staff, label: 'Scheduling', consumers: ['backoffice', 'crew', 'register'], legacy: 'scheduling_enabled',
  }),
  'labor.scheduling.show_on_register': atLocation(z.boolean(), false, {
    ...staff, label: 'Show the schedule on the register', consumers: ['register'], legacy: 'show_schedule_on_register',
  }),
  'labor.scheduling.week_start_day': atLocation(z.enum(['monday', 'sunday']), 'monday', {
    ...staff, label: 'Week starts on', consumers: ['backoffice', 'crew', 'register', 'core'], legacy: 'scheduling_week_start_day',
  }),
  'labor.scheduling.default_shift_start': atLocation(z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/), '09:00', {
    ...staff, label: 'Default shift start', consumers: ['backoffice'], legacy: 'scheduling_default_shift_start',
  }),
  // Legacy's form takes any number of hours ("Hours", `v-model.number`), so 7.5 is a valid default length.
  'labor.scheduling.default_shift_hours': atLocation(z.number().gt(0).max(24), 8, {
    ...staff, label: 'Default shift length (hours)', consumers: ['backoffice'], legacy: 'scheduling_default_shift_duration',
  }),
  // Settings › Staff Settings › Scheduling › Shift Rules (SETG-040). Legacy keeps hours as plain numbers and checks none of
  // them; the schedule warns past these (legacy ScheduleConflictDetector `min_rest_violation`, `overtime_warning`).
  'labor.scheduling.min_rest_hours': atLocation(z.number().min(0).max(24), 8, {
    ...staff, label: 'Minimum Rest Between Shifts (hours)', consumers: ['backoffice'], legacy: 'scheduling_min_rest_hours',
  }),
  'labor.scheduling.max_hours_week': atLocation(z.number().min(0).max(168), 40, {
    ...staff, label: 'Maximum Hours Per Week', consumers: ['backoffice'], legacy: 'scheduling_max_hours_week',
  }),
  // The breaks a new shift starts with (legacy `scheduling_default_breaks`: up to 10 lines of a break definition and
  // 1-10 of it). The import translates legacy's hash ids to the imported break definitions; a retired one is skipped
  // when a shift is pre-filled, and the page names how many are stale.
  'labor.scheduling.default_breaks': atLocation(z.array(z.object({ breakPolicyId: z.uuid({ version: 'v7' }), quantity: z.int().min(1).max(10) })).max(10), [], {
    ...staff, label: 'Default Breaks', consumers: ['backoffice'], legacy: null,
  }),
  'labor.scheduling.allow_open_shift_claims': atLocation(z.boolean(), true, {
    ...staff, label: 'Let employees claim open shifts', consumers: ['register', 'crew', 'server'], legacy: 'scheduling_allow_open_shift_claims',
  }),
  'labor.scheduling.require_claim_approval': atLocation(z.boolean(), false, {
    ...staff, label: 'Open shift claims need approval', consumers: ['register', 'crew', 'server'], legacy: 'scheduling_require_claim_approval',
  }),
  // Settings › Staff Settings › Scheduling › Employee Self-Service (SETG-041). Legacy gates new crew swap requests and the
  // Back Office's Shift Swap tab on the first (a manager's release follows the claim setting instead, as legacy's
  // ShiftSwapController::store does); the second says whether a coworker's accept waits for a manager (crew path).
  'labor.scheduling.allow_shift_swaps': atLocation(z.boolean(), true, {
    ...staff, label: 'Allow Shift Swaps', consumers: ['backoffice', 'crew'], legacy: 'scheduling_allow_shift_swaps',
  }),
  'labor.scheduling.require_swap_approval': atLocation(z.boolean(), true, {
    ...staff, label: 'Require Manager Approval for Swaps', consumers: ['crew'], legacy: 'scheduling_require_swap_approval',
  }),
  'labor.scheduling.time_off_notice_days': atLocation(z.int().min(0).max(365), 7, {
    ...staff, label: 'Time off notice (days)', consumers: ['register', 'crew', 'server'], legacy: 'scheduling_timeoff_advance_notice_days',
  }),
  'labor.scheduling.auto_approve_time_off': atLocation(z.boolean(), false, {
    ...staff, label: 'Auto-approve time off requests', consumers: ['register', 'crew', 'server'], legacy: 'scheduling_auto_approve_timeoff',
  }),
  'labor.scheduling.group_by_role': atLocation(z.boolean(), false, {
    // Settings › Staff Settings › Scheduling › Group by Employee Role: the schedule lists employees under their role, in
    // the roles' order (Employee Role Order, DLG-097), with Unassigned last.
    ...staff, label: 'Group by Employee Role', consumers: ['backoffice', 'crew'], legacy: 'scheduling_group_by_role',
  }),
  'ai.assistant.enabled': atLocation(z.boolean(), true, {
    permission: 'settings.location.manage', category: 'AI', label: 'AI assistant', consumers: ['backoffice', 'ai'], legacy: 'ai_assistant_enabled',
    origin: 'cloud', replication: 'cloud_only',
  }),
  // Legacy ImageTemplates module (LocationSettings.php: `ai_image_generation_enabled`, default false): whether the item
  // editor offers AI product images at this location (parity ai/, conversations-and-features.md). Carried at import so the
  // merchant's choice survives; its default template (`ai_image_generation_default_template_id`) waits on the
  // ImageTemplates import (legacy.ts marks it dead until then).
  'ai.image_generation.enabled': atLocation(z.boolean(), false, {
    permission: 'settings.location.manage', category: 'AI', label: 'AI image generation', consumers: ['backoffice', 'ai'], legacy: 'ai_image_generation_enabled',
    origin: 'cloud', replication: 'cloud_only',
  }),

  // ---- Location: profile (Business Profile › Location Info, SCR-125; legacy `locations` columns) ------------------------
  // The receipts' business block prints the address and phone (legacy `businessLocation`); the colour marks the
  // location in the switcher. Legacy requires every field but the phone (and saved even when one was empty, B-row FIX).
  // The name's home is the tenancy row (`tenancy.locations.name`); `tenancy.createLocation` and `updateLocation` copy it
  // here in the same settings fact so a rename reaches the location's devices (the register shows it in its header and
  // on receipts). Empty until the first create or rename in Radish Next: the register then shows the name it was claimed with.
  'location.profile.name': atLocation(z.string().max(255), '', {
    ...platform, category: 'Location Info', label: 'Location Name', consumers: ['register', 'receipts'], legacy: null,
  }),
  // The zone's home is also the tenancy row (`tenancy.locations.timezone`), copied here the same way so a zone change
  // reaches the registers: they compute business days, receipt times and time cards in it. Empty until the first create
  // or zone change in Radish Next: the register then keeps the zone it was claimed with.
  'location.profile.time_zone': atLocation(z.string().max(64), '', {
    ...platform, category: 'Location Info', label: 'Time Zone', consumers: ['register', 'receipts'], legacy: null,
  }),
  // Lane C round 9 (coordinator, 2026-10-15; IMPROVE L590): true exactly while the location is a training location
  // (`tenancy.locations.lifecycle_state = 'training'`, the migration's training tenant; staging's 'demo' stores are not). Its home is the tenancy row;
  // `tenancy.setLocationLifecycle` writes both in one transaction (a deferred trigger refuses a commit where they differ),
  // and `settings.set` refuses it. The register reads it from synced config, offline too, to show the training bar.
  'location.profile.training': atLocation(z.boolean(), false, {
    ...platform, category: 'Location Info', label: 'Training location', consumers: ['register'], legacy: null,
  }),
  // Lane X (coordinator, 2026-10-15): Radish staff open one live location's install to the cloud while its node is not
  // claimed yet (a store whose node is down at install): its units then sync with the cloud, as a training location's
  // do, until the node is claimed (core fleet `syncTargetOf`). Off by default, so no store loses the offline guarantee
  // unasked; staff only, and `settings.set` records the change in the audit log.
  'location.sync.cloud_install': atLocation(z.boolean(), false, {
    ...platform, category: 'Location Info', label: 'Units sync with the cloud until the node is claimed', consumers: ['backoffice'], legacy: null,
  }),
  // IMPROVE-BO-02's switch, mirroring legacy's per-location `clickhouse_reports_enabled` (ReportsHeader.vue allowTimeRange,
  // lane BO 2026-10-21, coordinator's decision): off, a report's time window applies to a one-day range only and offers
  // no weekdays, as legacy's TimeRange.vue without ClickHouse; on, the window and weekdays work on every range. Imported
  // from the legacy column, so each location keeps what it has today; turned on for every location after cutover.
  // Staff only, as legacy's toggle is (Clickhouse ToggleController, role:admin).
  'reports.daypart.every_range': atLocation(z.boolean(), false, {
    ...platform, category: 'Location Info', label: 'Report time window and weekdays on every date range', consumers: ['backoffice'],
    legacy: 'clickhouse_reports_enabled', store: 'locations',
  }),
  'location.profile.address_line1': atLocation(z.string().max(255), '', {
    permission: 'settings.location.manage', category: 'Location Info', label: 'Address', consumers: ['backoffice', 'register', 'receipts'], legacy: 'address', store: 'locations',
  }),
  'location.profile.city': atLocation(z.string().max(255), '', {
    permission: 'settings.location.manage', category: 'Location Info', label: 'City', consumers: ['backoffice', 'register', 'receipts'], legacy: 'city', store: 'locations',
  }),
  'location.profile.state': atLocation(z.string().max(255), '', {
    permission: 'settings.location.manage', category: 'Location Info', label: 'State', consumers: ['backoffice', 'register', 'receipts'], legacy: 'state', store: 'locations',
  }),
  'location.profile.zip': atLocation(z.string().max(32), '', {
    permission: 'settings.location.manage', category: 'Location Info', label: 'Zip', consumers: ['backoffice', 'register', 'receipts'], legacy: 'zip', store: 'locations',
  }),
  'location.profile.phone': atLocation(z.string().max(32), '', {
    permission: 'settings.location.manage', category: 'Location Info', label: 'Phone Number', consumers: ['backoffice', 'register', 'receipts'], legacy: 'phone', store: 'locations',
  }),
  'location.profile.contact_first_name': atLocation(z.string().max(255), '', {
    permission: 'settings.location.manage', category: 'Location Info', label: 'Contact First Name', consumers: ['backoffice'], legacy: 'first_name', store: 'locations',
  }),
  'location.profile.contact_last_name': atLocation(z.string().max(255), '', {
    permission: 'settings.location.manage', category: 'Location Info', label: 'Contact Last Name', consumers: ['backoffice'], legacy: 'last_name', store: 'locations',
  }),
  'location.profile.email': atLocation(z.string().max(255), '', {
    // Read-only on Location Info (legacy disables it): set with the account.
    ...platform, category: 'Location Info', label: 'Email', consumers: ['backoffice'], legacy: 'email', store: 'locations',
  }),
  'location.profile.color': atLocation(LocationColor, 'blue', {
    permission: 'settings.location.manage', category: 'Location Info', label: 'Location Color', consumers: ['backoffice'], legacy: 'default_label_color', store: 'locations',
  }),

  // ---- Location: pricing program, tax and rounding (legacy `locations` columns) ---------------------------------------
  'pricing.tax.default_rate': atLocation(Rate, rate(0n), {
    permission: 'settings.location.manage', category: 'Taxes', label: 'Sales tax %', consumers: ['core', 'register', 'receipts'],
    legacy: 'tax', store: 'locations',
  }),
  'pricing.program': atLocation(z.enum(['traditional', 'non_cash_adjustment', 'true_margin']), 'traditional', {
    ...platform, category: 'Payment Configuration', label: 'Program Type', consumers: ['core', 'register', 'receipts', 'reports'],
    legacy: 'program_type', store: 'locations',
  }),
  'pricing.cash_discount.enabled': atLocation(z.boolean(), false, {
    ...platform, category: 'Payment Configuration', label: 'Cash discount', consumers: ['core', 'register'], legacy: 'cash_discount', store: 'locations',
  }),
  'pricing.cash_discount.rate': atLocation(Rate, rate(0n), {
    ...platform, category: 'Payment Configuration', label: 'Cash Discount Rate', consumers: ['core', 'register', 'receipts'],
    legacy: 'cash_discount_fee', store: 'locations',
  }),
  'pricing.cash_discount.kind': atLocation(z.enum(['percent', 'fixed']), 'percent', {
    ...platform, category: 'Payment Configuration', label: 'Cash discount type', consumers: ['core', 'register'], legacy: 'cash_discount_type', store: 'locations',
  }),
  'pricing.true_margin.since': atLocation(z.iso.datetime().nullable(), null, {
    // When the location moved to True Margin (legacy `locations.nca_tm_conversion_date`, set by Radish staff): reports
    // before it are NCA/Other, after it True Margin (the Overview's Reporting Conflict and Deposit Summary). Null: never moved.
    ...platform, category: 'Payment Configuration', label: 'TrueMargin conversion date', consumers: ['reports', 'register'],
    legacy: 'nca_tm_conversion_date', store: 'locations',
  }),
  'pricing.true_margin.hide_cash_discount': atLocation(z.boolean(), false, {
    // "Disable cash discount at checkout": True Margin without cash discount. Saved only by admins (field notes).
    ...platform, category: 'Payment Configuration', label: 'Disable cash discount at checkout', consumers: ['core', 'register', 'receipts'],
    legacy: 'disable_cash_discount',
  }),
  // D1 round 41 (coordinator, lane V QA build 46): legacy's Split flags `settingEnableCustomProductPricesToggle` and
  // `defaultCashPriceToggleOn` become these location values, set by the import from the account's real flags (lane M,
  // tools/migrate flags/split.ts). Defaults are production's treatment, on for both. The first shows "With Cash
  // Discount" on True Margin custom-priced and misc items (ProductCustom.vue, ProductMisc.vue `displayPricesToggle`);
  // the second starts a new custom-priced or misc item on it, so the keyed amount is the cash price (ProductCustom
  // `resetPrice`, ProductMisc `mountedHook`; B1722). The key keeps its first name.
  'pricing.true_margin.cash_price_toggle': atLocation(z.boolean(), true, {
    ...platform, category: 'Payment Configuration', label: 'With Cash Discount on custom and misc items', consumers: ['register'], legacy: null,
  }),
  'pricing.true_margin.misc_starts_on_cash_price': atLocation(z.boolean(), true, {
    ...platform, category: 'Payment Configuration', label: 'Custom and misc items start on the cash price', consumers: ['register'], legacy: null,
  }),
  'pricing.true_margin.cash_rounding': atLocation(CashRounding, 'none', {
    // Legacy device key `cash_due_rounding` ("Round tax-exempt carts", True Margin only): the program's cash rounding.
    // As legacy, each register sets its own from its Settings dialog (Andrew, 2026-10-11: "do it how we do it today"); a
    // location value applies to registers without one.
    permission: 'hardware.manage', category: 'Payment Configuration', label: 'Round tax-exempt carts', consumers: ['core', 'register'],
    legacy: 'cash_due_rounding', store: 'devices.config', scope: ['location', 'device'], origin: 'device',
  }),
  // CD-P15 (coordinator 2026-10-14): the legacy split flag `fixWrongTicketTotalAdjustment`, rolled out to about a third
  // of accounts, becomes this location value, set by the import from the account's real flag. On: a True Margin split's
  // reconciling cents go to the last check with something due; off (most accounts): to the last check, paid or not.
  // Effective-dated so an order always re-prices with the value of its own business date (order pricing resolves settings
  // as of it): a change takes effect from the next business day and the history is kept (coordinator 2026-10-14).
  'pricing.true_margin.wrong_ticket_total_fix': {
    ...atLocation(z.boolean(), false, {
      ...platform, category: 'Payment Configuration', label: 'Split leftover cents skip paid checks', consumers: ['core', 'register'], legacy: null,
    }),
    effectiveDated: { history: true, effective: 'next_business_day' },
  },
  'cash.change_rounding': atLocation(z.enum(['off', 'nearest_nickel', 'round_up']), 'off', {
    ...checkout, category: 'Cash', label: 'Cash change rounding', consumers: ['core', 'server', 'register'], legacy: 'cash_change_rounding',
  }),

  // ---- Location: store-and-forward limits (doc 05, P3; mirror the processor, so cloud-authoritative) ------------------
  'payments.saf.enabled': atLocation(z.boolean(), false, {
    ...platform, category: 'Offline Payments', label: 'Store-and-forward card payments', consumers: ['register', 'server'], legacy: null,
  }),
  'payments.saf.max_count': atLocation(z.int().min(0).max(10_000), 100, {
    ...platform, category: 'Offline Payments', label: 'Most offline card payments held', consumers: ['register', 'server'], legacy: null,
  }),
  'payments.saf.max_total': atLocation(Money, money(50_000n, 'USD'), {
    ...platform, category: 'Offline Payments', label: 'Most offline card total held', consumers: ['register', 'server'], legacy: null,
  }),
  'payments.saf.max_age_hours': atLocation(z.int().min(1).max(168), 48, {
    ...platform, category: 'Offline Payments', label: 'Oldest offline card payment held (hours)', consumers: ['register', 'server'], legacy: null,
  }),

  // ---- Device: register behaviour (legacy `devices.config`) ---------------------------------------------------------
  // Register Training mode (ops plans/2026-10-19-register-training-mode): the one printer a training register's kitchen
  // tickets go to, for a kitchen that trains too. None by default: training sends no kitchen ticket.
  'register.training.kitchen_printer_id': atDevice(z.uuid().nullable(), null, {
    category: 'Printing', label: 'Training kitchen printer', consumers: ['register', 'backoffice'], legacy: null,
  }),
  'payments.cards.auth_then_tip': atDevice(z.boolean(), false, {
    category: 'Payments', label: 'Auth and tip', consumers: ['register'], legacy: 'auth_and_tip',
  }),
  'payments.cards.manual_entry': atDevice(z.boolean(), true, {
    category: 'Payments', label: 'Manual card entry', consumers: ['register'], legacy: 'manual_card_entry',
  }),
  'payments.tenders.gift_card_enabled': atDevice(z.boolean(), false, {
    category: 'Payments', label: 'Gift cards', consumers: ['register'], legacy: 'gift_cards',
  }),
  'payments.tenders.checks_enabled': atDevice(z.boolean(), false, {
    category: 'Payments', label: 'Checks', consumers: ['register'], legacy: 'checks',
  }),
  'payments.tenders.gift_card_tips': atDevice(z.boolean(), false, {
    category: 'Payments', label: 'Gift card tips', consumers: ['register'], legacy: 'gift_card_tips',
  }),
  // FLG-021 / FLG-024: the legacy split flags `settingCloverGiftCardAlgorithm` / `settingSingleTrackGiftCardAlgorithm`
  // become one location setting: which magnetic-stripe format the location's gift cards use.
  'payments.tenders.gift_card_swipe_format': atLocation(z.enum(['two_track', 'clover', 'single_track']), 'two_track', {
    ...checkout, category: 'Payments', label: 'Gift card reader format', consumers: ['register'], legacy: null,
  }),
  'orders.till.share_orders_across_drawers': atDevice(z.boolean(), false, {
    category: 'Orders', label: 'Sync orders between drawers', consumers: ['register', 'server'], legacy: 'sync_orders_between_drawers',
  }),
  'orders.till.show_only_mine': atDevice(z.boolean(), true, {
    category: 'Orders', label: 'Show only my orders', consumers: ['register'], legacy: 'show_only_my_orders',
  }),
  'customers.attach_enabled': atDevice(z.boolean(), false, {
    category: 'Customers', label: 'Customers', consumers: ['register'], legacy: 'customers',
  }),
  'devices.cfd.paired': atDevice(z.boolean(), false, {
    category: 'Customer Display', label: 'Customer display screen', consumers: ['register'], legacy: 'customer_display_screen',
  }),
  // Which KDS printer's tickets this screen shows: its station (legacy `devices.config['kds_printer_id']`, a printer
  // hashid; the import maps it to the printer's id, so `legacy: null` here). The screen picks it on its unlinked page,
  // and the backoffice sets it in the device's Kitchen Display settings or Add/Edit Device. Null: unlinked.
  'kds.station.printer_id': atKds(z.uuid().nullable(), null, 'KDS printer'),
  'kds.appearance.theme': atKds(z.enum(['light', 'dark']), 'light', 'Theme'),
  'kds.appearance.layout': atKds(z.enum(['grid', 'full']), 'grid', 'Ticket layout'),
  'kds.appearance.header_fields': atKds(
    z.object({ order_number: z.boolean(), customer_name: z.boolean(), employee_name: z.boolean(), time_placed: z.boolean(), dining_option: z.boolean(), table_name: z.boolean() }),
    { order_number: true, customer_name: true, employee_name: true, time_placed: true, dining_option: true, table_name: true },
    'Header fields',
  ),
  // Legacy's choices are Small (sm), Medium (md) and Large, which is its `xl`.
  'kds.appearance.text_size': atKds(z.enum(['sm', 'md', 'xl']), 'sm', 'Item text size'),
  'kds.appearance.timer_format': atKds(z.enum(['mm:ss', 'HH:mm']), 'mm:ss', 'Order timer format'),
  'kds.appearance.single_line_modifiers': atKds(z.boolean(), false, 'Single-line modifiers'),
  'kds.appearance.bump_bar_shortcuts': atKds(z.boolean(), false, 'Show bump bar shortcuts button'),
  'kds.behavior.auto_complete': atKds(z.boolean(), false, 'Auto-complete finished tickets'),
  'kds.behavior.double_tap_complete': atKds(z.boolean(), false, 'Double-tap to complete'),
  'kds.behavior.check_modifiers': atKds(z.boolean(), false, 'Check off modifiers'),
  'kds.behavior.completed_items_down': atKds(z.boolean(), false, 'Move completed items down'),
  'kds.behavior.auto_rush_moved_up': atKds(z.boolean(), false, 'Auto-rush moved-up orders'),
  'kds.timing.enabled': atKds(z.boolean(), true, 'Enable ticket timing'),
  'kds.timing.approaching_minutes': atKds(KdsMinutes, 5, 'Approaching delay'),
  'kds.timing.delayed_minutes': atKds(KdsMinutes, 20, 'Delayed'),
  'kds.timing.sound_when_delayed': atKds(z.boolean(), true, 'Play sound when delayed'),
  'kds.printer.enabled': atKds(z.boolean(), false, 'Enable printer'),
  'kds.printer.address': atKds(z.string().trim().max(64), '', 'Printer IP address'),
  'kds.printer.print_size': atKds(z.enum(['sm', 'md', 'lg']), 'sm', 'Print size'),
  'kds.printer.auto_print': atKds(z.boolean(), false, 'Auto-print on complete'),
  'kds.sound.enabled': atKds(z.boolean(), true, 'Enable sound'),
  'kds.sound.volume': atKds(z.int().min(0).max(100), 100, 'Alert volume'),
  'kds.sound.new_order': atKds(KdsSound, 'ding', 'New order sound'),
  'kds.sound.error': atKds(KdsSound, 'error', 'Error sound'),
  'kds.general.sync_across_devices': atKds(z.boolean(), false, 'Sync settings across devices'),
  'kds.general.language': atKds(z.enum(['en-US', 'es']), 'en-US', 'Display language'),
  'register.tables.page_enabled': atDevice(z.boolean(), false, {
    category: 'Tables', label: 'Tables page', consumers: ['register'], legacy: 'table_mapping',
  }),
  'register.navigation.home_page': atDevice(z.string().regex(/^[a-z][a-z_]{0,39}$/), 'till', {
    category: 'Navigation', label: 'Home page', consumers: ['register'], legacy: 'homepage_selection',
  }),
  'register.payment.skip_confirmation': atDevice(
    z.object({ cash: z.boolean(), card: z.boolean(), gift_card: z.boolean(), other: z.boolean() }),
    { cash: false, card: false, gift_card: false, other: false },
    { category: 'Payments', label: 'Skip the confirmation screen after a transaction', consumers: ['register'], legacy: 'skip_confirmation_screen_after_transaction' },
  ),
  'register.payment.combine_cash_buttons': atDevice(z.boolean(), false, {
    category: 'Payments', label: 'Combine the Cash and Quick Cash buttons', consumers: ['register'], legacy: 'combine_cash_and_quick_cash_button',
  }),
  // QR Table Pay › Settings (legacy QRTableSettings.vue, `dining_template_mappings` per online store; SETG-064): which
  // of the location's dining options a QR table order takes for "For Here" and for "To Go", and the name the diner
  // sees. Legacy saved these and nothing read them (B794); Radish Direct reads them for a QR order (Direct plan,
  // contracts Direct must honour). Written together by `online.setQrDiningMapping`, which refuses another location's
  // dining option; the migration import writes legacy's mappings through it (tools/migrate transform/online.ts).
  'online.qr.for_here.dining_option_id': atLocation(z.uuid().nullable(), null, {
    permission: 'qr.manage', category: 'QR Table Pay', label: 'For Here dining option', consumers: ['backoffice', 'online'], legacy: null,
  }),
  'online.qr.for_here.label': atLocation(z.string().max(255), '', {
    permission: 'qr.manage', category: 'QR Table Pay', label: 'For Here customer preview', consumers: ['backoffice', 'online'], legacy: null,
  }),
  // Legacy's form starts To Go on (`is_active: true` before a first save).
  'online.qr.to_go.enabled': atLocation(z.boolean(), true, {
    permission: 'qr.manage', category: 'QR Table Pay', label: 'Enable To Go', consumers: ['backoffice', 'online'], legacy: null,
  }),
  'online.qr.to_go.dining_option_id': atLocation(z.uuid().nullable(), null, {
    permission: 'qr.manage', category: 'QR Table Pay', label: 'To Go dining option', consumers: ['backoffice', 'online'], legacy: null,
  }),
  'online.qr.to_go.label': atLocation(z.string().max(255), '', {
    permission: 'qr.manage', category: 'QR Table Pay', label: 'To Go customer preview', consumers: ['backoffice', 'online'], legacy: null,
  }),
  'online.orders.receive_on_device': atDevice(z.boolean(), false, {
    category: 'Online Ordering', label: 'Online ordering', consumers: ['register'], legacy: 'online_ordering',
  }),
  'cash.drawer.multiple_per_device': atDevice(z.boolean(), false, {
    category: 'Cash Drawer', label: 'Multiple drawers', consumers: ['register'], legacy: 'multiple_drawers',
  }),
  'cash.drawer.allow_reopen': atDevice(z.boolean(), false, {
    category: 'Cash Drawer', label: 'Reopen drawers', consumers: ['register'], legacy: 'reopen_drawers',
  }),
  'cash.drawer.blind_drop_print': atDevice(z.boolean(), true, {
    category: 'Cash Drawer', label: 'Print the blind drop', consumers: ['register'], legacy: 'blind_drop_print',
  }),
  'cash.drawer.auto_open_after_payment': atDevice(
    z.object({ cash: z.boolean(), card: z.boolean(), check: z.boolean(), other: z.boolean() }),
    { cash: true, card: false, check: false, other: false },
    { category: 'Cash Drawer', label: 'Open the cash drawer after payment', consumers: ['register'], legacy: 'auto_open_cash_drawer_after_payment' },
  ),

  // ---- Device: receipts ---------------------------------------------------------------------------------------------
  'receipts.customer.itemized': atDevice(z.boolean(), true, {
    category: 'Receipts', label: 'Itemized receipts', consumers: ['receipts'], legacy: 'receipt_print_itemized',
  }),
  'receipts.customer.footer': atDevice(z.string().max(500), '', {
    category: 'Receipts', label: 'Receipt footer', consumers: ['receipts'], legacy: 'receipt_footer',
  }),
  'receipts.customer.print_size': atDevice(ReceiptSize, 'md', {
    category: 'Receipts', label: 'Receipt print size', consumers: ['receipts'], legacy: 'receipt_print_size',
  }),
  'receipts.customer.copies': atDevice(z.int().min(1).max(5), 1, {
    category: 'Receipts', label: 'Receipt copies', consumers: ['receipts'], legacy: 'receipt_print_count',
  }),
  'receipts.customer.show_modifier_prices': atDevice(z.boolean(), false, {
    category: 'Receipts', label: 'Show modifier prices on receipts', consumers: ['receipts'], legacy: 'show_modifier_price_on_receipt',
  }),
  'receipts.customer.auto_print_after_payment': atDevice(
    z.object({ cash: z.boolean(), card: z.boolean(), other: z.boolean() }),
    { cash: false, card: false, other: false },
    { category: 'Receipts', label: 'Print a receipt after the transaction', consumers: ['register', 'receipts'], legacy: 'auto_print_receipt_after_transaction' },
  ),
  'receipts.digital.email_enabled': atDevice(z.boolean(), false, {
    category: 'Receipts', label: 'Email receipts', consumers: ['register'], legacy: 'digital_receipts',
  }),
  'receipts.kitchen.show_order_number': atDevice(z.boolean(), true, {
    category: 'Kitchen Receipts', label: 'Show the order number on kitchen receipts', consumers: ['receipts'], legacy: 'kitchen_receipt_show_order_number',
  }),
  // ---- lane D3: kitchen tickets (register settings-consumed.md §1.7, §2 Kitchen; hardware.md §5.15, §6) ----------------
  'receipts.kitchen.use_sort_priority': atLocation(z.boolean(), true, {
    ...checkout, category: 'Printing', label: 'Sort kitchen receipt items by category order', consumers: ['register'],
    legacy: 'kitchen_receipt_use_sort_priority',
  }),
  'receipts.kitchen.show_employee': atDevice(z.boolean(), false, {
    category: 'Kitchen Receipts', label: 'Show the employee on kitchen receipts', consumers: ['receipts'], legacy: 'receipt_show_employee',
  }),
  'receipts.kitchen.copies': atDevice(z.int().min(0).max(5), 1, {
    category: 'Kitchen Receipts', label: 'Kitchen receipt copies', consumers: ['register'], legacy: 'kitchen_receipt_print_count',
  }),
  'receipts.kitchen.allow_zero_quantity': atDevice(z.boolean(), true, {
    category: 'Kitchen Receipts', label: 'Print zero-quantity items', consumers: ['register'], legacy: 'kitchen_receipt_allow_zero_quantity',
  }),
  'receipts.kitchen.include_x_in_quantity': atDevice(z.boolean(), true, {
    category: 'Kitchen Receipts', label: 'Include "x" in the quantity', consumers: ['receipts'], legacy: 'kitchen_receipt_include_x_in_quantity',
  }),
  'receipts.kitchen.header_separator': atDevice(z.boolean(), true, {
    category: 'Kitchen Receipts', label: 'Separator line under the header', consumers: ['receipts'], legacy: 'kitchen_receipt_use_header_separator',
  }),
  'receipts.kitchen.line_separator': atDevice(z.boolean(), false, {
    category: 'Kitchen Receipts', label: 'Separator line between items', consumers: ['receipts'], legacy: 'kitchen_receipt_use_line_separator',
  }),
  /**
   * B1865 (legacy register 82da7ff0c, e346239dd, 7e5b3413b; api 8a9f8b639): each ticket of a printer set to itemize
   * prints "1 of 3" at its foot, numbered among the tickets one send gives that physical printer. Off by default.
   */
  'receipts.kitchen.show_itemized_receipt_count': atDevice(z.boolean(), false, {
    category: 'Kitchen Receipts', label: 'Show receipt count on itemized printers', consumers: ['register', 'receipts'],
    legacy: 'kitchen_receipt_show_itemized_receipt_count',
  }),
  'receipts.kitchen.all_modifiers_red': atDevice(z.boolean(), false, {
    category: 'Kitchen Receipts', label: 'Print all modifiers in red', consumers: ['receipts'], legacy: 'receipt_all_modifiers_red',
  }),
  /**
   * Legacy rings the bell (a drawer pulse on the network printer) after every kitchen print unless a split flag is on
   * and this setting is explicitly false. With the flag off fleet-wide, the effective default is to ring.
   */
  'receipts.kitchen.ring_bell': atDevice(z.boolean(), true, {
    category: 'Kitchen Receipts', label: 'Ring the bell when a kitchen receipt prints', consumers: ['register'], legacy: 'ring_bell_on_kitchen_receipt_print',
  }),
  'receipts.kitchen.include_customer_info': atDevice(z.boolean(), false, {
    category: 'Kitchen Receipts', label: 'Customer info on kitchen receipts', consumers: ['register', 'receipts'], legacy: 'kitchen_receipt_include_customer_info',
  }),
  'receipts.kitchen.show_prices_and_discounts': atDevice(z.boolean(), false, {
    category: 'Kitchen Receipts', label: 'Prices and discounts on kitchen receipts', consumers: ['receipts'],
    legacy: 'kitchen_receipt_show_prices_and_discounts',
  }),
  'receipts.kitchen.print_before_payment': atDevice(z.boolean(), true, {
    category: 'Kitchen Receipts', label: 'Send to the kitchen before payment', consumers: ['register'], legacy: 'kitchen_receipt_print_before_payment',
  }),
  /** Printers this device never prints to or sends KDS tickets to (legacy `kitchen_printer_ban`, a list of `{id}`). */
  'receipts.kitchen.banned_printer_ids': atDevice(z.array(z.uuid({ version: 'v7' })).max(50), [], {
    category: 'Kitchen Receipts', label: 'Printers this device never prints to', consumers: ['register'], legacy: null,
  }),
  // ---- lane D1: catalog & ordering (register settings-consumed.md LS-001..LS-047, DS-002..DS-022) ----
  'catalog.modifiers.use_included': atLocation(z.boolean(), true, {
    ...checkout, category: 'Modifiers', label: 'Use included modifiers', consumers: ['register', 'core'], legacy: 'use_included_modifiers',
  }),
  'catalog.modifiers.included_on_top': atLocation(z.boolean(), false, {
    ...checkout, category: 'Modifiers', label: 'Sort included modifiers on top', consumers: ['register', 'core'], legacy: 'sort_included_modifiers_on_top',
  }),
  // Off by default, as legacy's LocationSettings: a new order takes the default dining option unless the location asks.
  'orders.dining_option.prompt': atLocation(z.boolean(), false, {
    ...checkout, category: 'Dining Options', label: 'Prompt Dining Option selection (disable default)', consumers: ['register'], legacy: 'dining_option_prompt',
  }),
  // Legacy DiningOptionsPopupStore `promptDiningOptions`: choosing one of these dining options on an order with no tab
  // prompts for a customer (register LS-002). From legacy `dining_option_customer_select` (whole dining option objects),
  // whose hash ids the import translates (legacy.ts).
  'orders.dining_option.customer_select': atLocation(z.array(z.string().max(64)).max(100), [], {
    ...checkout, category: 'Dining Options', label: 'Auto prompt user to select a customer', consumers: ['register'], legacy: null,
  }),
  'orders.items.availability_mode': atLocation(z.boolean(), false, {
    ...checkout, category: 'Orders', label: 'Item management mode', consumers: ['register'], legacy: 'item_management_mode',
  }),
  'orders.id_verification.enabled': atLocation(z.boolean(), false, {
    ...checkout, category: 'Orders', label: 'ID verification', consumers: ['register'], legacy: 'id_verification',
  }),
  'orders.gratuity.manual_enabled': atLocation(z.boolean(), true, {
    ...tips, label: 'Manual gratuity', consumers: ['register'], legacy: 'manual_gratuity_enabled',
  }),
  'register.dining_option.default': atDevice(z.string().max(64).nullable(), null, {
    category: 'Dining Options', label: 'Default dining option', consumers: ['register'], legacy: null,
  }),
  'register.product_card.grouped_modifiers': atDevice(z.boolean(), false, {
    category: 'Appearance', label: 'Grouped modifiers product card', consumers: ['register'], legacy: 'grouped_modifiers_product_card',
  }),
  'register.till.quantity_selector': atDevice(z.boolean(), false, {
    category: 'Appearance', label: 'Till quantity selector', consumers: ['register'], legacy: 'till_quantity_selector',
  }),
  'register.till.hidden_category_ids': atDevice(z.array(z.string().max(64)).max(500), [], {
    category: 'Appearance', label: 'Categories hidden on this register', consumers: ['register'], legacy: null,
  }),

  // ---- Device: the rest of legacy DeviceSettings (lane D6; ops parity/register/settings-consumed.md §2) ----------------
  // Labels are the backoffice device-settings dialog's (field note backoffice.settings.hardware.devices).
  'register.appearance.dark_mode': atDevice(z.boolean(), false, {
    category: 'Appearance', label: 'Dark Mode', consumers: ['register'], legacy: 'dark_mode',
  }),
  // The till's layout and columns also take a location value, which every register there starts from until it saves its
  // own (ops plan 2026-10-17-launchpad-menu-build §4.4: Launchpad's menu build saves the layout the rep picked).
  'register.categories.menu_left': atDevice(z.boolean(), true, {
    category: 'Appearance', label: 'Category Menu location', consumers: ['register'], legacy: 'navigation_left', scope: LAYOUT_SCOPE,
  }),
  'register.categories.fixed_menu': atDevice(z.boolean(), true, {
    category: 'Appearance', label: 'Fixed Category Menu', consumers: ['register'], legacy: 'fixed_category_menu', scope: LAYOUT_SCOPE,
  }),
  'register.categories.continuous_scroll': atDevice(z.boolean(), false, {
    category: 'Appearance', label: 'Continuous scrolling to fixed category menu', consumers: ['register'], legacy: 'fixed_category_continuous_scroll', scope: LAYOUT_SCOPE,
  }),
  'register.till.columns': atDevice(z.int().min(4).max(6), 5, {
    // Handheld is forced to 2 columns and tablet to 4 by the register, whatever is stored.
    category: 'Appearance', label: 'Column Count', consumers: ['register'], legacy: 'till_columns', scope: LAYOUT_SCOPE,
  }),
  'orders.name.required': atDevice(z.boolean(), false, {
    category: 'Functionality', label: 'Require Order Name', consumers: ['register'], legacy: 'require_order_name',
  }),
  'register.tabs.enabled': atDevice(z.boolean(), true, {
    category: 'Functionality', label: 'Enable Tabs', consumers: ['register'], legacy: 'tabs',
  }),
  'customers.search.phone_keyboard_input': atDevice(z.boolean(), false, {
    category: 'Functionality', label: 'Keyboard input for customer phone search', consumers: ['register'], legacy: 'customer_search_by_phone_keyboard_input',
  }),
  'register.cart.send_button': atDevice(z.boolean(), true, {
    category: 'Functionality', label: 'Send Button', consumers: ['register'], legacy: 'send_button',
  }),
  'register.cart.stay_button': atDevice(z.boolean(), false, {
    category: 'Functionality', label: 'Stay Button', consumers: ['register'], legacy: 'stay_button',
  }),
  'register.sign_out.after_transaction': atDevice(z.boolean(), false, {
    category: 'Functionality', label: 'Auto log out user after each transaction', consumers: ['register'], legacy: 'logout_after_transaction',
  }),
  'register.sign_out.after_send': atDevice(z.boolean(), false, {
    category: 'Functionality', label: 'Auto log out after Send/Save order', consumers: ['register'], legacy: 'logout_after_send',
  }),
  'register.sign_out.idle_seconds': atDevice(z.int().min(0).max(600).refine((s) => IDLE_SECONDS.includes(s), 'one of 0, 30, 60, 300, 600'), 0, {
    category: 'Functionality', label: 'Auto log out user idle time', consumers: ['register'], legacy: 'logout_idle_time',
  }),
  'catalog.price_adjustments.update_quantity_prompt': atDevice(z.boolean(), false, {
    category: 'Functionality', label: 'Price Adjustments Update Quantity pop-up', consumers: ['register'], legacy: 'price_adjustment_update_qty',
  }),
  'orders.checks.lock_paid': atDevice(z.boolean(), false, {
    // Takes effect only with the location's split checks on.
    category: 'Functionality', label: 'Lock checks containing payments', consumers: ['register'], legacy: 'beta_lock_paid_ticket_items',
  }),
  'payments.tenders.house_account_enabled': atDevice(z.boolean(), false, {
    category: 'Payment', label: 'House Accounts', consumers: ['register'], legacy: 'house_accounts',
  }),
  'receipts.customer.show_order_number': atDevice(z.boolean(), true, {
    category: 'Receipts', label: 'Print order', consumers: ['register', 'receipts'], legacy: 'receipt_show_order_number',
  }),
  'receipts.customer.show_modifier_set_names': atDevice(z.boolean(), true, {
    category: 'Receipts', label: 'Show modifier set names on customer receipts', consumers: ['receipts'], legacy: 'show_modifier_set_names_on_customer_receipts',
  }),
  'receipts.customer.suggested_tips': atDevice(z.boolean(), false, {
    // Printed only with Collect tips from customers on.
    category: 'Receipts', label: 'Display suggested tips', consumers: ['receipts'], legacy: 'show_suggestion_tip',
  }),
  'receipts.customer.print_after_tip': atDevice(z.boolean(), false, {
    category: 'Receipts', label: 'Print Final Receipt After Tip', consumers: ['register'], legacy: 'print_final_receipt_after_tip',
  }),
  'receipts.printer.default_port': atDevice(z.string().max(255), '', {
    category: 'Receipts', label: 'Default Receipt Printer Port', consumers: ['register'], legacy: 'default_receipt_printer_portname',
  }),
  'receipts.merchant.every_card_purchase': atDevice(z.boolean(), false, {
    category: 'Receipts', label: 'Merchant receipt on every card purchase', consumers: ['register'], legacy: 'merchant_receipt_every_card_purchase',
  }),
  'receipts.merchant.with_customer_copy': atDevice(z.boolean(), true, {
    category: 'Receipts', label: 'Merchant receipt with the customer copy', consumers: ['register'], legacy: 'merchant_receipt_with_customer_copy',
  }),
  'receipts.merchant.with_reprint': atDevice(z.boolean(), true, {
    category: 'Receipts', label: 'Merchant receipt with a reprint', consumers: ['register'], legacy: 'merchant_receipt_with_reprint',
  }),
  'cash.drawer.blind_drop': atDevice(z.boolean(), false, {
    category: 'Drawers', label: 'Blind Drop', consumers: ['register'], legacy: 'blind_drop',
  }),
  'cash.drawer.require_denominations': atDevice(z.boolean(), false, {
    category: 'Drawers', label: 'Require denomination entry when opening/closing drawers', consumers: ['register'], legacy: 'require_denomination_for_drawer_entry_method',
  }),
  'register.sign_out.clear_cart': atDevice(z.boolean(), false, {
    category: 'Beta', label: 'Empty the cart when employee logs out', consumers: ['register'], legacy: 'clear_cart_on_logout',
  }),
  'orders.checks.print_on_send': atDevice(z.boolean(), false, {
    category: 'Beta', label: 'Print checks when sending an order', consumers: ['register'], legacy: 'print_checks_on_send',
  }),
  'payments.cards.force_return_to_app': atDevice(z.boolean(), false, {
    // The register forces it on for tablets and when a customer display is attached.
    category: 'Payment', label: 'Return to the app after each card transaction', consumers: ['register'], legacy: 'force_return_to_app_after_transaction',
  }),
  'devices.cfd.force_attached': atDevice(z.boolean(), false, {
    category: 'Customer Display', label: 'Treat a customer display as attached', consumers: ['register'], legacy: 'force_has_cfd',
  }),
});

/**
 * The settings that price a sale or move money, declared fail-closed (lane RG, B606 follow-up; SettingDef `failClosed`).
 * A reader that cannot decode one holds rather than sell on the default. Each choice, and why (the test
 * `setting-fail-closed.test.ts` holds that every key core's pricing reads is here):
 * - every `pricing.*` key and `orders.tax_profile.exclusive_item_override`: the tax, the program and the cash discount;
 * - `catalog.modifiers.use_included`: whether included modifiers are charged;
 * - `orders.gratuity.*` and `cash.change_rounding`: amounts added to an order or handed back;
 * - the tip presets and `payments.tenders.gift_card_tips`: the tips offered and what a gift card may tip;
 * - `orders.tabs.offline_unpaid_cap` and `payments.saf.max_total`, `max_count`, `max_age_hours`: offline money limits, which a
 *   default would loosen;
 * - `labor.tips.*` and `cash.server_banks.closeout_without_tip_payout`: who is paid which tips, and when;
 * - `labor.breaks.back_office_overrun`: how much of a back-office break is paid.
 * Not fail-closed: `catalog.modifiers.included_on_top` (display order), `payments.cards.auth_then_tip` (a flow; every amount
 * is still keyed), `business_day.start_time` (which day a sale reports on, effective-dated; no amount changes), the other
 * `cash.server_banks.*` keys (flows and who may do what), and `payments.saf.enabled` (its default, off, is already the safe one).
 */
const FAIL_CLOSED = [
  'pricing.tax.default_rate', 'pricing.program', 'pricing.cash_discount.enabled', 'pricing.cash_discount.rate', 'pricing.cash_discount.kind',
  'pricing.true_margin.since', 'pricing.true_margin.hide_cash_discount', 'pricing.true_margin.cash_price_toggle', 'pricing.true_margin.misc_starts_on_cash_price',
  'pricing.true_margin.cash_rounding', 'pricing.true_margin.wrong_ticket_total_fix', 'orders.tax_profile.exclusive_item_override',
  'catalog.modifiers.use_included',
  'orders.gratuity.auto_enabled', 'orders.gratuity.auto_rate', 'orders.gratuity.auto_threshold', 'orders.gratuity.auto_dining_option_ids', 'orders.gratuity.manual_enabled',
  'cash.change_rounding', 'payments.tips.custom_presets', 'payments.tips.use_default_presets', 'payments.tenders.gift_card_tips',
  'orders.tabs.offline_unpaid_cap', 'payments.saf.max_total', 'payments.saf.max_count', 'payments.saf.max_age_hours',
  'labor.tips.pooling_enabled', 'labor.tips.paid_in_enabled', 'labor.tips.payroll_from_tenders', 'cash.server_banks.closeout_without_tip_payout',
  'labor.breaks.back_office_overrun',
] as const satisfies readonly (keyof typeof declared)[];

const failClosedKeys: ReadonlySet<string> = new Set(FAIL_CLOSED);
export const settings = Object.fromEntries(
  Object.entries(declared).map(([key, def]) => [key, failClosedKeys.has(key) ? { ...def, failClosed: true as const } : def]),
) as typeof declared;

export type SettingKey = keyof typeof settings;

/**
 * Who controls a key's value instead of the register (`SettingDef.lockedBy`, DS-077), or undefined when the register may
 * change it. Every register write of its own settings refuses a locked key (`setOwnSetting`), and the Settings dialog
 * shows it locked; unknown keys are never locked. `registry` is the settings registry (a test passes its own).
 */
export const settingLockedBy = (key: string, registry: Readonly<Record<string, Pick<SettingDef, 'lockedBy'>>> = settings): string | undefined =>
  Object.hasOwn(registry, key) ? registry[key]?.lockedBy : undefined;
